'use client'

import React from 'react'
import { Badge, Box, Button, Flex, HStack, Link, Text } from '@chakra-ui/react'
import { MapLocation } from '..'
import { LuArrowRight, LuX } from 'react-icons/lu'

const Stars: React.FC<{ rating?: number }> = ({ rating }) => {
  const clamped = Math.max(0, Math.min(5, rating ?? 0))
  const rounded = Math.round(clamped * 2) / 2
  const full = Math.floor(rounded)
  const half = rounded - full === 0.5
  return (
    <HStack gap="1px" mt="4px">
      {Array.from({ length: 5 }).map((_, i) => {
        const type = i < full ? 'full' : i === full && half ? 'half' : 'empty'
        return (
          <Box as="span" key={i} lineHeight={0}>
            {type === 'empty' && (
              <svg width="16" height="16" viewBox="0 0 24 24">
                <g fill="none" stroke="#E5E7EB" strokeWidth="2">
                  <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </g>
              </svg>
            )}
            {type === 'full' && (
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path
                  fill="#EAB308"
                  d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                />
              </svg>
            )}
            {type === 'half' && (
              <svg width="16" height="16" viewBox="0 0 24 24">
                <g fill="none" stroke="#E5E7EB" strokeWidth="2">
                  <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </g>
                <defs>
                  <clipPath id="halfClip">
                    <rect x="0" y="0" width="12" height="24" />
                  </clipPath>
                </defs>
                <path
                  clipPath="url(#halfClip)"
                  fill="#EAB308"
                  d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                />
              </svg>
            )}
          </Box>
        )
      })}
    </HStack>
  )
}

export const LocationsMapPopup: React.FC<{
  loc: MapLocation
  onClose?: () => void
}> = ({ loc, onClose }) => {
  const destination = `${loc.latitude},${loc.longitude}`
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&output=embed`
  const when = loc.opensInFuture ? loc.opensInFutureDate ?? '' : null
  const meta: string[] = []
  if (typeof loc.distanceKm === 'number') meta.push(`${Math.round(loc.distanceKm)}Km`)
  if (typeof loc.travelTimeMin === 'number') meta.push(`${Math.round(loc.travelTimeMin)}m`)

  return (
    <Box
      w="420px"
      maxW="90vw"
      borderRadius="14px"
      bg="white"
      boxShadow="lg"
      p="16px"
      fontFamily="system-ui, -apple-system, Segoe UI, Roboto, Noto Sans, Arial"
      color="#0f172a"
      zIndex={1000}
    >
      <Flex align="start" justify="space-between">
        <Box>
          <Link key={loc.id} href={`/locations/${loc.id}`} display="block">
            <Text fontWeight="700" fontSize="18px" lineHeight="24px">
              {loc.name}
            </Text>
          </Link>
          <Stars rating={loc.rating} />
          {loc.address && (
            <Text color="#6b7280" fontSize="13px" mt="4px">
              {loc.address}
            </Text>
          )}
          {loc.opensInFuture && (
            <Badge
              mt="8px"
              px="10px"
              py="4px"
              borderRadius="9999px"
              bg="#F3F4F6"
              color="#111827"
              fontSize="12px"
              fontWeight="600"
            >
              {when ? `Åpner ${when}` : 'Åpner snart'}
            </Badge>
          )}
        </Box>

        <Button
          aria-label="Lukk"
          onClick={onClose}
          w="32px"
          h="32px"
          minW="32px"
          borderRadius="full"
          bg="#111827"
          color="white"
          _hover={{ bg: '#0b1220' }}
        >
          <LuX size={35} strokeWidth={1.5} />
        </Button>
      </Flex>

      <Box h="1px" bg="#e5e7eb" my="12px" />

      <HStack gap="18px">
        <Link href={mapsUrl} flex="1">
          <Button
            display="inline-flex"
            alignItems="center"
            gap="8px"
            px="12px"
            py="10px"
            borderRadius="full"
            bg="#F4E1B5"
            _hover={{ bg: '#f2d8a3' }}
          >
            <Box as="span" lineHeight={0}>
              <LuArrowRight size={20} />
            </Box>
            Veibeskrivelse
          </Button>
        </Link>
      </HStack>

      <Box h="1px" bg="#e5e7eb" my="12px" />

      <Flex align="center" justify="space-between" wrap="wrap" gap="12px">
        <HStack gap="12px">
          {meta.length > 0 && (
            <Text color="#111827" fontWeight="600">
              {meta.join(' — ')}
            </Text>
          )}
          {loc.locationArticleId && loc.id != null && (
            <Link key={loc.id} href={`/locations/${loc.id}`} display="block">
              <Button as="a" size="sm" bg="#D97706" color="white" borderRadius="8px" _hover={{ bg: '#c86b05' }}>
                Les mer
              </Button>
            </Link>
          )}
        </HStack>
      </Flex>
    </Box>
  )
}

export default LocationsMapPopup
